// src/pages/Admin/Views/Referrals/CommissionPlansList.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, ShieldAlert } from 'lucide-react';

// API Service
import { getCommissionPlans } from '../../../../services/referralApiServices';

// shadcn/ui component imports
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { useToast } from '../../../../hooks/use-toast'; 

const CommissionPlansList = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { toast } = useToast();

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const data = await getCommissionPlans();
            setPlans(data || []);
        } catch (error) {
            toast({ 
                variant: "destructive", 
                title: "Error", 
                description: "Failed to load commission plans." 
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    if (loading) {
        return <div className="p-8 text-center text-muted-foreground">Loading commission plans...</div>;
    }

    return (
        <div className="p-6 space-y-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Commission Plans</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Manage referral payout rules for Affiliates, Sellers, and Employees.
                    </p>
                </div>
                <Button onClick={() => navigate('/admin/referrals/commission-plans/new')}>
                    <Plus className="w-4 h-4 mr-2" /> Create New Plan
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>All Active & Historic Plans</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="border rounded-md">
                        <Table>
                            <TableHeader className="bg-muted/50">
                                <TableRow>
                                    <TableHead>Plan Name</TableHead>
                                    <TableHead>Target Partner</TableHead>
                                    <TableHead>Target Product</TableHead>
                                    <TableHead className="text-center">Tiers</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {plans.map((plan) => (
                                    <TableRow key={plan.id}>
                                        <TableCell className="font-medium text-foreground">
                                            {plan.name}
                                            {plan.description && (
                                                <div className="text-xs text-muted-foreground font-normal mt-0.5 truncate max-w-[250px]">
                                                    {plan.description}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                                {plan.target_partner_type || "ALL"}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {plan.target_product_subtype ? (
                                                <Badge variant="secondary">Subtype: {plan.target_product_subtype}</Badge>
                                            ) : plan.target_product_type ? (
                                                <Badge variant="secondary">Type: {plan.target_product_type}</Badge>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">Any Product</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <span className="font-semibold">{plan.tiers?.length || 0}</span>
                                        </TableCell>
                                        <TableCell>
                                            {plan.is_active ? (
                                                <Badge variant="default" className="bg-green-600 hover:bg-green-700">Active</Badge>
                                            ) : (
                                                <Badge variant="secondary" className="text-gray-500">Inactive</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                onClick={() => navigate(`/admin/referrals/commission-plans/edit/${plan.id}`)}
                                            >
                                                <Edit className="w-4 h-4 mr-2 text-muted-foreground" /> Edit
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                
                                {plans.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} className="h-32 text-center">
                                            <div className="flex flex-col items-center justify-center text-muted-foreground">
                                                <ShieldAlert className="w-8 h-8 mb-2 opacity-20" />
                                                <p>No commission plans found.</p>
                                                <p className="text-xs mt-1">Click "Create New Plan" to get started.</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default CommissionPlansList;